import React, { useState } from 'react';
import { saveTrade } from '../../services/storage';

const AGENT_LIST = [
  { id: 'A-ICT',        icon: '🎯', weight: 4 },
  { id: 'A-Structure',  icon: '📐', weight: 2 },
  { id: 'A-Fibonacci',  icon: '🔢', weight: 2 },
  { id: 'A-Pattern',    icon: '🕯️', weight: 1 },
  { id: 'A-Liquidity',  icon: '💧', weight: 1 },
  { id: 'A-News',       icon: '📰', weight: 1 },
  { id: 'A-LivePrice',  icon: '💰', weight: 1 },
  { id: 'A-Session',    icon: '🕐', weight: 1 },
  { id: 'A-Risk',       icon: '🛡️', weight: 1 },
  { id: 'A-Psychology', icon: '🧘', weight: 1 },
  { id: 'A-Statistics', icon: '📊', weight: 1 },
  { id: 'A-Journal',    icon: '📓', weight: 1 },
];

function AgentBar({ agent, verdict, confidence, weight }) {
  const color = verdict === 'BUY' ? '#10b981' : verdict === 'SELL' ? '#ef4444' : '#f59e0b';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 60px', alignItems: 'center', gap: '10px', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
        <span>{agent.icon}</span>
        <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '600' }}>{agent.id}</span>
      </div>
      <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '4px', overflow: 'hidden', height: '6px' }}>
        <div style={{ width: `${confidence}%`, height: '100%', background: color, borderRadius: '4px', transition: 'width 0.8s ease' }}/>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', justifyContent: 'flex-end' }}>
        <span style={{ fontSize: '11px', color, fontWeight: '700' }}>{verdict}</span>
        <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)' }}>{confidence}%</span>
      </div>
    </div>
  );
}

export default function TradeDetailModal({ signal, onClose }) {
  const [saved, setSaved] = useState(false);
  const [marked, setMarked] = useState(false);

  if (!signal) return null;

  const isLive = signal.entry && signal.sl;
  const vc     = signal.verdict === 'BUY' ? '#10b981' : signal.verdict === 'SELL' ? '#ef4444' : '#f59e0b';

  // Build agent consensus from votes or synthesize
  const votes  = signal.details?.votes || signal.votes || [];
  const agentData = AGENT_LIST.map(a => {
    const vote = votes.find(v => v.src === a.id);
    const conf = vote
      ? (vote.dir === signal.verdict ? 65 + Math.round(a.weight * 8) : 30)
      : (signal.confidence ? Math.round(signal.confidence * 0.8 + Math.random() * 15) : 60);
    return {
      ...a,
      verdict:    vote?.dir || signal.verdict,
      confidence: Math.min(100, conf),
    };
  });

  const handleSaveJournal = () => {
    if (!signal.entry) return;
    saveTrade({
      direction:   signal.verdict,
      entry:       signal.entry,
      sl:          signal.sl,
      tp:          signal.tp1,
      result:      'OPEN',
      pnl_usd:     0,
      setup_type:  'AI_SIGNAL',
      session:     signal.session,
      date:        new Date().toISOString().split('T')[0],
      notes:       `JARVIS V5 signal. Grade ${signal.grade}. Confidence ${signal.confidence}%. ${signal.reason}`,
      source:      'JARVIS V5',
    });
    setSaved(true);
  };

  return (
    <div style={{
      position:   'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.85)',
      backdropFilter: 'blur(8px)',
      display:    'flex', alignItems: 'center', justifyContent: 'center',
      padding:    '16px',
    }} onClick={(e) => e.target === e.currentTarget && onClose()}>

      <div style={{
        background:   'linear-gradient(135deg, #0d1117 0%, #0a0f1e 100%)',
        border:       `1px solid ${vc}44`,
        borderRadius: '20px',
        width:        '100%', maxWidth: '600px',
        maxHeight:    '90vh', overflowY: 'auto',
        boxShadow:    `0 0 60px ${vc}22`,
        padding:      '28px',
      }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
              <div style={{ background: vc, color: '#000', padding: '4px 14px', borderRadius: '8px', fontWeight: '900', fontSize: '16px', letterSpacing: '2px' }}>
                {signal.verdict}
              </div>
              <span style={{ color: '#e2e8f0', fontWeight: '800', fontSize: '17px' }}>XAU/USD</span>
              {signal.grade && <span style={{ color: vc, fontWeight: '800', fontSize: '14px', background: `${vc}15`, padding: '2px 10px', borderRadius: '6px', border: `1px solid ${vc}33` }}>{signal.grade}</span>}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '12px' }}>{new Date().toLocaleString('en-IN')}</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#9ca3af', cursor: 'pointer', padding: '8px 12px', fontSize: '14px' }}>✕</button>
        </div>

        {/* Trade levels */}
        {isLive && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '20px' }}>
            {[
              { label: 'ENTRY',  val: signal.entry, color: '#60a5fa' },
              { label: 'SL',     val: signal.sl,    color: '#ef4444' },
              { label: 'TP1',    val: signal.tp1,   color: '#10b981' },
              { label: 'TP2',    val: signal.tp2,   color: '#34d399' },
            ].map(({ label, val, color }) => (
              <div key={label} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.3)', letterSpacing: '1.5px', marginBottom: '5px' }}>{label}</div>
                <div style={{ fontSize: '14px', fontWeight: '800', color, fontFamily: 'monospace' }}>{val ? `$${val}` : '—'}</div>
              </div>
            ))}
          </div>
        )}

        {signal.lotSize && signal.rrRatio && (
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
            {[
              { label: 'LOT SIZE', val: signal.lotSize },
              { label: 'R:R RATIO', val: `${signal.rrRatio}:1` },
              { label: 'CONFIDENCE', val: `${signal.confidence}%` },
              { label: 'SESSION', val: signal.session || '—' },
            ].map(({ label, val }) => (
              <div key={label} style={{ flex: 1, background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px', marginBottom: '3px' }}>{label}</div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#e2e8f0' }}>{val}</div>
              </div>
            ))}
          </div>
        )}

        {/* Why this level */}
        <div style={{ background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.12)', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '2px', color: '#00d4ff', marginBottom: '10px' }}>WHY THIS LEVEL IS IMPORTANT</div>
          {signal.keyConfluence?.length > 0 ? (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {signal.keyConfluence.map((k, i) => (
                <li key={i} style={{ fontSize: '13px', color: '#cbd5e1', padding: '3px 0', display: 'flex', gap: '8px' }}>
                  <span style={{ color: '#00d4ff' }}>•</span>{k}
                </li>
              ))}
            </ul>
          ) : (
            <p style={{ margin: 0, fontSize: '13px', color: '#cbd5e1', lineHeight: '1.7' }}>{signal.reason || 'ICT/SMC confluence detected at this level.'}</p>
          )}
        </div>

        {/* Immediate action */}
        {signal.immediateAction && (
          <div style={{ background: `${vc}08`, border: `1px solid ${vc}22`, borderRadius: '10px', padding: '12px 16px', marginBottom: '20px' }}>
            <span style={{ fontSize: '10px', color: vc, fontWeight: '700', letterSpacing: '1px' }}>IMMEDIATE ACTION  </span>
            <span style={{ fontSize: '13px', color: '#e2e8f0' }}>{signal.immediateAction}</span>
          </div>
        )}

        {/* Agent consensus */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '12px' }}>
            12 AGENTS CONSENSUS — {signal.agentsAgreed}/{signal.agentsTotal} AGREE
          </div>
          {agentData.map(a => (
            <AgentBar key={a.id} agent={a} verdict={a.verdict} confidence={a.confidence} weight={a.weight} />
          ))}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {!marked && isLive && (
            <button onClick={() => setMarked(true)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: vc, color: '#000', cursor: 'pointer', fontWeight: '800', fontSize: '13px' }}>
              Mark as TAKEN
            </button>
          )}
          {marked && <div style={{ flex: 1, padding: '12px', borderRadius: '10px', background: 'rgba(16,185,129,0.1)', color: '#10b981', textAlign: 'center', fontSize: '13px', fontWeight: '700' }}>✓ Marked as Taken</div>}
          <button onClick={handleSaveJournal} disabled={saved} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid rgba(0,212,255,0.3)', background: 'transparent', color: saved ? '#10b981' : '#00d4ff', cursor: saved ? 'default' : 'pointer', fontWeight: '700', fontSize: '13px' }}>
            {saved ? '✓ Saved to Journal' : 'Save to Journal'}
          </button>
          <button onClick={onClose} style={{ padding: '12px 20px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: '#6b7280', cursor: 'pointer', fontSize: '13px' }}>
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
